import React, { useState, useEffect } from "react";
import { FlashcardCollection, Flashcard, User, FlashcardType } from "../types";
import { buildExternalPrompt } from "../utils/PromptBuilder";
import { parseAIResponse } from "../utils/JsonSanitizer";
import { exportToPDF } from "../utils/pdfExport";
import { FaBolt, FaPlus, FaTrash, FaSave, FaMagic, FaExternalLinkAlt, FaRobot, FaDownload, FaTags } from "react-icons/fa";
import { ShareButtons } from "./ShareButtons";
import "../styles_1/FlashcardGenerator.css";

interface FlashcardGeneratorProps {
  onSaveCollection: (collection: FlashcardCollection) => void;
  user: User | null;
  onShowAuth: () => void;
}

export const FlashcardGenerator: React.FC<FlashcardGeneratorProps> = ({
  onSaveCollection,
  user,
  onShowAuth
}) => {
  const [activeTab, setActiveTab] = useState<"manual" | "ai">("ai");
  const [collectionName, setCollectionName] = useState("");
  const [deckTags, setDeckTags] = useState("");

  // --- AI MODE STATE ---
  const [notes, setNotes] = useState("");
  const [subject, setSubject] = useState("");
  const [focusTopic, setFocusTopic] = useState("");
  const [counts, setCounts] = useState({ concept: 5, app: 3, mcq: 5, cloze: 5 });
  const [jsonInput, setJsonInput] = useState("");
  const [showPromptPanel, setShowPromptPanel] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(1);

  // --- MANUAL MODE STATE ---
  const [manualType, setManualType] = useState<FlashcardType>("concept");
  const [manualFront, setManualFront] = useState("");
  const [manualBack, setManualBack] = useState("");
  const [manualOptions, setManualOptions] = useState({ A: "", B: "", C: "", D: "" });
  const [manualCorrect, setManualCorrect] = useState("A");

  // LIST OF CARDS
  const [generatedCards, setGeneratedCards] = useState<Flashcard[]>(() => {
    const saved = localStorage.getItem("flashgen_cards");
    return saved ? JSON.parse(saved) : [];
  });

  // PERSISTENCE LOGIC
  useEffect(() => {
    localStorage.setItem("flashgen_notes", notes);
    localStorage.setItem("flashgen_subject", subject);
    localStorage.setItem("flashgen_topic", focusTopic);
    localStorage.setItem("flashgen_name", collectionName);
    localStorage.setItem("flashgen_cards", JSON.stringify(generatedCards));
  }, [notes, subject, focusTopic, collectionName, generatedCards]);

  // LOAD INITIAL
  useEffect(() => {
    setNotes(localStorage.getItem("flashgen_notes") || "");
    setSubject(localStorage.getItem("flashgen_subject") || "");
    setFocusTopic(localStorage.getItem("flashgen_topic") || "");
    setCollectionName(localStorage.getItem("flashgen_name") || "");
  }, []);

  // --- AI LOGIC ---
  const handleGeneratePrompt = () => {
    if (!notes.trim()) return alert("Please paste your study notes first!");
    setShowPromptPanel(true);
  };

  const currentPrompt = buildExternalPrompt(notes, { subject, focusTopic, counts });

  const handleProcessJson = () => {
    try {
      const cards = parseAIResponse(jsonInput);
      setGeneratedCards([...generatedCards, ...cards]);
      setJsonInput("");
      setShowPromptPanel(false);
      alert(`${cards.length} cards added to deck!`);
    } catch (e: any) {
      alert(e.message || "Failed to parse JSON. Ensure it's a valid array format.");
    }
  };

  const copyAndRedirect = (url: string) => {
    navigator.clipboard.writeText(currentPrompt);
    alert("Prompt copied to clipboard! Redirecting...");
    window.open(url, "_blank");
  };

  // --- MANUAL LOGIC ---
  const handleAddManualCard = () => {
    if (!manualFront.trim()) return alert("Front/Question is required");

    const newCard: Flashcard = {
      id: `manual-${Date.now()}`,
      front: manualFront,
      back: manualType === "mcq" ? manualBack : manualBack, // Simplified for now; MCQ handling in UI
      status: "new",
      type: manualType,
      ...(manualType === "mcq" && {
        options: [manualOptions.A, manualOptions.B, manualOptions.C, manualOptions.D],
        correctAnswer: manualCorrect
      })
    };

    setGeneratedCards([...generatedCards, newCard]);
    setManualFront("");
    setManualBack("");
    setManualOptions({ A: "", B: "", C: "", D: "" });
  };

  const handleSaveDeck = () => {
    if (!user) return onShowAuth();

    let finalName = collectionName.trim();
    if (!finalName) {
      const promptedName = window.prompt("Your deck needs a name before saving. Enter it below:", "My UPSC Deck");
      if (!promptedName) return;
      finalName = promptedName;
      setCollectionName(finalName);
    }

    if (generatedCards.length === 0) return alert("Add at least one card");

    const newCollection: FlashcardCollection = {
      id: Date.now().toString(),
      name: finalName,
      flashcards: generatedCards,
      isLocked: false,
      revisionSchedule: { nextRevision: new Date().toISOString(), interval: 1 },
      tags: deckTags.split(',').map(t => t.trim()).filter(t => t !== "")
    };

    onSaveCollection(newCollection);
    setGeneratedCards([]);
    setCollectionName("");
    localStorage.removeItem("flashgen_cards");
    alert("Deck successfully saved to your profile!");
  };

  return (
    <div className="generator">
      <div className="generator-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="generator-title">Deck Builder</h1>
            <p className="generator-subtitle">UPSC-Standard Flashcard Generator</p>
          </div>
          <button
            className="secondary-btn"
            onClick={() => { setShowTutorial(true); setTutorialStep(1); }}
            style={{ padding: '8px 16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: 'full' }}
          >
            <FaMagic style={{ color: '#16a34a' }} /> Tutorial
          </button>
        </div>
      </div>

      <div className="mode-tabs">
        <button
          className={`mode-tab ${activeTab === 'ai' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai')}
        >
          <FaRobot style={{ marginRight: '8px' }} /> AI Assistant
        </button>
        <button
          className={`mode-tab ${activeTab === 'manual' ? 'active' : ''}`}
          onClick={() => setActiveTab('manual')}
        >
          <FaPlus style={{ marginRight: '8px' }} /> Manual Entry
        </button>
      </div>

      {/* DECK METADATA (STICKY) */}
      <div className="metadata-bar">
        <div className="input-group">
          <label style={{ color: '#e11d48' }}><FaTags /> Deck Name <span style={{ fontSize: '0.7rem', background: '#e11d48', color: 'white', padding: '2px 6px', borderRadius: '4px', marginLeft: '8px' }}>IMPORTANT</span></label>
          <input
            type="text"
            className={`text-input ${!collectionName && generatedCards.length > 0 ? 'error-pulse' : ''}`}
            placeholder="REQUIRED: e.g., Indian Economy: Fiscal Policy"
            value={collectionName}
            onChange={(e) => setCollectionName(e.target.value)}
          />
        </div>
        <div className="input-group">
          <label>Deck Tags (Comma separated)</label>
          <input
            type="text"
            className="text-input"
            placeholder="UPSC, Economy, Mains"
            value={deckTags}
            onChange={(e) => setDeckTags(e.target.value)}
          />
        </div>
      </div>

      {activeTab === "ai" ? (
        <>
          <div className="input-section">
            <div className={`section-header ${tutorialStep === 1 && showTutorial ? 'tutorial-highlight' : ''}`}>
              <h2><FaMagic /> 1. Paste Study Notes</h2>
            </div>
            <textarea
              className={`notes-input ${tutorialStep === 1 && showTutorial ? 'tutorial-highlight' : ''}`}
              rows={8}
              placeholder="Paste text from PDFs, newspapers, or websites here..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <div className="section-header" style={{ marginTop: '30px' }}>
              <h2><FaBolt /> 2. Set Context & Quantities</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label>Subject</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="e.g., Economy, History"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div>
                <label>Focus Topic</label>
                <input
                  type="text"
                  className="text-input"
                  placeholder="e.g., Fiscal Policy"
                  value={focusTopic}
                  onChange={(e) => setFocusTopic(e.target.value)}
                />
              </div>
            </div>

            <div className="count-slider-group">
              <div className="count-input">
                <label>Concept</label>
                <input type="number" value={counts.concept} onChange={(e) => setCounts({ ...counts, concept: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="count-input">
                <label>Cloze</label>
                <input type="number" value={counts.cloze} onChange={(e) => setCounts({ ...counts, cloze: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="count-input">
                <label>Application</label>
                <input type="number" value={counts.app} onChange={(e) => setCounts({ ...counts, app: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="count-input">
                <label>MCQ</label>
                <input type="number" value={counts.mcq} onChange={(e) => setCounts({ ...counts, mcq: parseInt(e.target.value) || 0 })} />
              </div>
            </div>

            <button className={`${tutorialStep === 2 && showTutorial ? 'tutorial-highlight' : ''} generate-btn`} onClick={handleGeneratePrompt}>
              Build UPSC Prompt
            </button>
          </div>

          <div className={`launch-pad-section ${tutorialStep === 3 && showTutorial ? 'tutorial-highlight' : ''}`}>
            <h3><FaExternalLinkAlt /> 3. Redirect to AI</h3>
            <p>The prompt is optimized for UPSC. Click a button to copy & go:</p>
            <div className="llm-buttons">
              <button className="llm-btn" onClick={() => copyAndRedirect("https://chatgpt.com")}>
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5153-4.9108 6.0462 6.0462 0 0 0-4.7471-3.124 5.9867 5.9867 0 0 0-5.4385 1.4151 5.9861 5.9861 0 0 0-5.1107 1.343 6.0464 6.0464 0 0 0-1.8398 4.7061 5.9881 5.9881 0 0 0-1.3429 5.1106 6.0462 6.0462 0 0 0 3.1241 4.7471 5.9867 5.9867 0 0 0 5.4385-1.4152 5.9861 5.9861 0 0 0 5.1107-1.3429 6.0463 6.0463 0 0 0 1.8398-4.7062 5.9881 5.9881 0 0 0 1.3429-5.1106 6.0463 6.0463 0 0 0-3.124-4.7471ZM12.1014 17.1415a2.6682 2.6682 0 0 1-1.3543-.3715l-.1056-.0621-3.197 1.8456a.7925.7925 0 0 1-1.185-.758v-3.6912l-.015-.0088a2.6898 2.6898 0 0 1-1.3262-2.3129 2.6683 2.6683 0 0 1 1.336-2.3088l.1056-.0621-1.8456-3.197a.7925.7925 0 0 1 .758-1.185h3.6912l.0088-.0151a2.6898 2.6898 0 0 1 2.3129-1.3263 2.6682 2.6682 0 0 1 2.3088 1.336l.062 1.0566 3.197-1.8456a.7925.7925 0 0 1 1.185.758v3.6912l.0151.0088a2.6898 2.6898 0 0 1 1.3263 2.3129 2.6682 2.6682 0 0 1-1.3361 2.3088l-.1055.0621 1.8456 3.197a.7925.7925 0 0 1-.758 1.185h-3.6912l-.0088.015a2.6898 2.6898 0 0 1-2.3129 1.3263 2.6682 2.6682 0 0 1-1.3543-.3715ZM10.285 14.1923l-2.022-1.1675v-2.335l2.022-1.1675 2.022 1.1675v2.335l-2.022 1.1675Z" fill="#10a37f" />
                </svg>
                <span>ChatGPT</span>
              </button>
              <button className="llm-btn" onClick={() => copyAndRedirect("https://gemini.google.com")}>
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" fill="url(#gemini_grad)" />
                  <defs>
                    <linearGradient id="gemini_grad" x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#4182FF" />
                      <stop offset="1" stopColor="#A464FF" />
                    </linearGradient>
                  </defs>
                </svg>
                <span>Gemini</span>
              </button>
              <button className="llm-btn" onClick={() => copyAndRedirect("https://www.perplexity.ai")}>
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L13.8 8.2H20.2L15 12L16.8 18.2L11.6 14.4L6.4 18.2L8.2 12L3 8.2H9.4L11.2 2H12.8" stroke="#1fb8cd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="12" r="3" fill="#1fb8cd" />
                </svg>
                <span>Perplexity</span>
              </button>
            </div>

            <div style={{ marginTop: '40px' }} className={tutorialStep === 4 && showTutorial ? 'tutorial-highlight' : ''}>
              <label>4. Paste AI JSON Output Below</label>
              <textarea
                className="notes-input"
                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                rows={6}
                placeholder="Paste the JSON code block here..."
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
              />
              <button className="generate-btn" style={{ background: '#16a34a' }} onClick={handleProcessJson}>
                Convert to Cards
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="input-section">
          <div className="section-header">
            <h2>Add Manual Card</h2>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            {["concept", "cloze", "application", "mcq"].map(t => (
              <button
                key={t}
                className={`secondary-btn ${manualType === t ? 'active' : ''}`}
                style={manualType === t ? { background: '#0f172a', color: 'white' } : {}}
                onClick={() => setManualType(t as FlashcardType)}
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          <label>Front / Question</label>
          <textarea className="text-input" rows={2} value={manualFront} onChange={(e) => setManualFront(e.target.value)} />

          {manualType === "mcq" ? (
            <div style={{ marginTop: '20px' }}>
              <label>Options</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {['A', 'B', 'C', 'D'].map(L => (
                  <div key={L} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontWeight: 'bold' }}>{L}</span>
                    <input className="text-input" value={manualOptions[L as keyof typeof manualOptions]} onChange={(e) => setManualOptions({ ...manualOptions, [L]: e.target.value })} />
                    <input type="radio" checked={manualCorrect === L} onChange={() => setManualCorrect(L)} />
                  </div>
                ))}
              </div>
              <label style={{ marginTop: '15px' }}>Explanation (Back)</label>
              <textarea className="text-input" rows={3} value={manualBack} onChange={(e) => setManualBack(e.target.value)} />
            </div>
          ) : (
            <div style={{ marginTop: '20px' }}>
              <label>Back / Answer</label>
              <textarea className="text-input" rows={4} value={manualBack} onChange={(e) => setManualBack(e.target.value)} />
            </div>
          )}

          <button className="generate-btn" style={{ marginTop: '30px' }} onClick={handleAddManualCard}>
            <FaPlus /> Add to Collection
          </button>
        </div>
      )}

      {/* COLLECTION PREVIEW */}
      {generatedCards.length > 0 && (
        <div className="input-section" style={{ marginTop: '40px' }}>
          <h3>Deck Preview ({generatedCards.length} cards)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {generatedCards.map((c, i) => (
              <div key={c.id} style={{ padding: '12px', border: '1px solid #e2e8f0', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '0.9rem' }}>
                  <span className={`card-badge ${c.type}`} style={{ position: 'static', marginRight: '8px' }}>{c.type}</span>
                  <strong>{i + 1}.</strong> {c.front.substring(0, 80)}...
                </div>
                <button
                  onClick={() => setGeneratedCards(generatedCards.filter(cd => cd.id !== c.id))}
                  style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                >
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '20px' }}>
            <button className="generate-btn" onClick={handleSaveDeck}>
              <FaSave /> Save to Profile
            </button>
            <ShareButtons title={`Mastering UPSC with this ${collectionName || 'AI'} deck on ScoreSmart!`} />
            <button
              className="generate-btn"
              style={{ background: '#0f172a' }}
              onClick={() => {
                if (!user) return onShowAuth();
                exportToPDF({ name: collectionName || "UPSC_Deck", flashcards: generatedCards, id: 'temp', isLocked: false, revisionSchedule: { nextRevision: '', interval: 1 } });
              }}
            >
              <FaDownload /> Download PDF
            </button>
          </div>
        </div>
      )}

      {/* TUTORIAL OVERLAY */}
      {showTutorial && (
        <div className="tutorial-overlay">
          <div className="tutorial-content">
            <div className="tutorial-step-card">
              <h3>Step {tutorialStep} of 5</h3>
              <p>
                {tutorialStep === 1 && "Start by pasting your UPSC study notes, PDF text, or news articles in this area."}
                {tutorialStep === 2 && "Configure your deck settings and click here to compile a tailor-made UPSC prompt."}
                {tutorialStep === 3 && "Click any logo to copy your prompt and open the AI. Paste the prompt there to get JSON."}
                {tutorialStep === 4 && "Once the AI gives you the JSON code block, paste it here and click 'Convert' to finish!"}
                {tutorialStep === 5 && "IMPORTANT: Always export your data using the 'Export All Data' button in the sidebar after your session to save your progress locally!"}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                <button className="secondary-btn" onClick={() => setShowTutorial(false)}>Close</button>
                {tutorialStep < 5 ? (
                  <button className="generate-btn" style={{ margin: 0 }} onClick={() => setTutorialStep(tutorialStep + 1)}>Next Step</button>
                ) : (
                  <button className="generate-btn" style={{ margin: 0, background: '#16a34a' }} onClick={() => setShowTutorial(false)}>Got It!</button>
                )}
              </div>
            </div>
            {/* Animated Arrows */}
            <div className={`tutorial-arrow step-${tutorialStep}`}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
